import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import classnames from 'classnames'
import { Select, Checkbox, Button } from 'mailup-web-components'
import deviceSizes from './support/deviceSizes'
import './Tabs.scss'
import { notifyOpener } from 'Helpers/notifyParent'

export const Tabs = ({ children, className }) => {
  return <ul className={classnames('c-preview-tabs', className)}>{children}</ul>
}

Tabs.propTypes = {
  children: PropTypes.any,
  className: PropTypes.string
}

const TabLabel = ({ label, onClick }) => {
  const handleClick = e => {
    e.preventDefault()
    if (typeof onClick === 'function') {
      onClick(e)
    }
  }

  return (
    <a className='c-preview-tab__label' href='#' onClick={handleClick}>
      <span>{label}</span>
    </a>
  )
}
TabLabel.propTypes = {
  onClick: PropTypes.func,
  label: PropTypes.string
}

export const Tab = ({
  children,
  className,
  active,
  onClick,
  label,
  'data-mu-test': dataAttributeTest
}) => (
  <li
    className={classnames('c-preview-tab', className, {
      'c-preview-tab--active': active,
      'c-preview-tab--with-label': label
    })}
    data-mu-test={dataAttributeTest}
  >
    {label && <TabLabel label={label} onClick={onClick} />}
    {children && <div className='c-preview-tab__inner'>{children}</div>}
  </li>
)

Tab.propTypes = {
  children: PropTypes.any,
  className: PropTypes.string,
  label: PropTypes.string,
  onClick: PropTypes.func,
  active: PropTypes.bool,
  'data-mu-test': PropTypes.string
}

export const TabsDesktopOrMobile = ({
  active,
  onClickTab,
  onChangeSelect,
  selectValue,
  showMobile
}) => {
  const intl = useIntl()
  const handleClickTab = value => {
    onClickTab(value)
  }
  return (
    <>
      <Tab
        active={active === 'desktop'}
        className='c-preview-tab--desktop'
        data-mu-test={classnames('desktop-tab', {
          'desktop-tab--active': active === 'desktop'
        })}
        onClick={() => handleClickTab('desktop')}
        label={intl.formatMessage({
          id: 'Load.PreviewViewer.Desktop'
        })}
      />
      {showMobile && (
        <Tab
          active={active === 'mobile'}
          className='c-preview-tab--mobile'
          data-mu-test={classnames('mobile-tab', {
            'mobile-tab--active': active === 'mobile'
          })}
          onClick={() => handleClickTab('mobile')}
          label={intl.formatMessage({
            id: 'Load.PreviewViewer.Mobile'
          })}
        >
          <Select
            searchable={false}
            value={selectValue}
            onChange={onChangeSelect}
            options={deviceSizes.map(size => ({
              label: `${size.label} (${size.width} x ${size.height})`,
              value: size.id
            }))}
          />
        </Tab>
      )}
    </>
  )
}

TabsDesktopOrMobile.defaultProps = {
  showMobile: false
}

TabsDesktopOrMobile.propTypes = {
  active: PropTypes.string,
  onClickTab: PropTypes.func,
  onChangeSelect: PropTypes.func,
  selectValue: PropTypes.string,
  showMobile: PropTypes.bool
}

export const TabAllPreviews = ({ idMessage, idList }) => {
  const intl = useIntl()

  return (
    <Tab
      className='c-preview-tab--all-previews'
      label={intl.formatMessage({
        id: 'Load.PreviewViewer.AllPreviews'
      })}
      onClick={() => {
        notifyOpener('setCurrentList', [
          idList,
          `messages_app_redirector.aspx?r=${encodeURIComponent(
            `/messages/email/${idMessage}/previews`
          )}`,
          true
        ])

        window.opener && window.close()
      }}
    />
  )
}

TabAllPreviews.propTypes = {
  idMessage: PropTypes.string.isRequired,
  idList: PropTypes.number.isRequired
}

export const TabShowDynamicContent = ({ items, onConfirm, initialValues }) => {
  const intl = useIntl()
  const [visible, setVisible] = useState(false)
  const [values, setValues] = useState(initialValues)

  const handleChange = e => {
    e.persist()
    if (e.target.checked) {
      setValues(prev => [...prev, parseInt(e.target.value)])
      return
    }
    setValues(prev => prev.filter(v => v !== parseInt(e.target.value)))
  }

  const handleCheckAll = e => {
    e.persist()
    if (e.target.checked) {
      setValues(items.map(item => item.id))
      return
    }
    setValues([])
  }

  const allChecked = items.every(item => values.includes(item.id))

  const handleConfirm = () => {
    onConfirm(values)
    setVisible(false)
  }

  const handleToggle = () => {
    setVisible(!visible)
  }

  return (
    <Tab
      className='c-preview-tab--show-dynamic'
      label={intl.formatMessage({
        id: 'Load.PreviewViewer.SimulateDynamicContent'
      })}
      onClick={handleToggle}
    >
      <div
        className={classnames('c-preview-tab__menu', {
          'c-preview-tab__menu--visible': visible
        })}
      >
        <Checkbox
          label={intl.formatMessage({ id: 'Load.MailupCommon.BtnSelectAll' })}
          onChange={handleCheckAll}
          checked={allChecked}
        />
        {items.map(({ name, id }) => (
          <p key={id}>
            <Checkbox
              label={name}
              labelClassName='c-preview-tab--show-dynamic__checkbox__label'
              value={id}
              onChange={handleChange}
              checked={values.includes(id)}
            />
          </p>
        ))}
        <Button kind='secondary' onClick={handleConfirm}>
          {intl.formatMessage({ id: 'Load.PreviewViewer.Apply' })}
        </Button>
      </div>
    </Tab>
  )
}

TabShowDynamicContent.defaultProps = {
  initialValues: []
}

TabShowDynamicContent.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, id: PropTypes.number })
  ),
  initialValues: PropTypes.arrayOf(PropTypes.number),
  onConfirm: PropTypes.func
}
